import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { fmtDateTime, fmtDate, money } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Switch } from "@/components/ui/switch";

const KIND = { receipt: "Payment receipt", reminder: "Payment reminder", past_due: "Past-due notice", due_today: "Due-date pay link", ach_failed: "Bank payment failed", pay_link: "Pay link (sent by admin)", certificate: "Paid-in-full certificate", payoff_letter: "Payoff letter" };

export const ReminderToggle = ({ loan, onChanged }) => {
  const [on, setOn] = useState(loan.send_reminders !== false);
  const [busy, setBusy] = useState(false);
  const change = async (v) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/loans/${loan.id}/reminders`, { enabled: v });
      setOn(data.send_reminders);
      toast.success(`Reminders ${data.send_reminders ? "on" : "off"} for ${loan.loan_number}`);
      onChanged?.();
    } catch (e) {
      toast.error(`Couldn't save: ${apiError(e).message}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <label className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium">
      <Switch checked={on} disabled={busy} onCheckedChange={change} data-testid="loan-send-reminders-switch" /> Send reminders
    </label>
  );
};

export const EmailLog = ({ loanId, refreshKey }) => {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get(`/loans/${loanId}/emails`).then((r) => setItems(r.data.items)); }, [loanId, refreshKey]);
  if (!items) return <div className="px-6 py-10 text-sm text-muted-foreground">Loading…</div>;
  if (!items.length) return <div className="px-6 py-12 text-center text-sm text-muted-foreground" data-testid="emails-empty">No receipts, reminders or notices sent yet.</div>;
  return (
    <ul className="divide-y" data-testid="loan-email-log">
      {items.map((d, i) => (
        <li key={i} className="space-y-1 px-6 py-4" data-testid={`loan-email-${i}`}>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            <StatusBadge status={d.status} />
            <span className="font-semibold" data-testid={`loan-email-kind-${i}`}>{KIND[d.kind] || d.kind}{d.installment ? ` · installment ${d.installment}` : ""}</span>
            <span className="num text-xs text-muted-foreground">{fmtDateTime(d.at)} ET</span>
          </div>
          <div className="text-xs text-muted-foreground">
            to <b className="text-foreground">{d.recipient}</b> · {d.subject}
            {d.amount_cents != null && ` · ${money(d.amount_cents)}`}{d.due_date && ` · due ${fmtDate(d.due_date)}`}
          </div>
          {d.error && <div className="text-xs text-destructive" data-testid={`loan-email-error-${i}`}>{d.error}</div>}
        </li>
      ))}
    </ul>
  );
};
